import { Gender } from "@./cat-calculations";
import {
  formatCatNames,
  formatPossessiveCatNames,
  formatPossessiveName,
} from "libs/i18n/src/lib/formatting";

interface IPronouns {
  pronoun: string;
  possessivePronoun: string;
  objectPronoun: string;
  contractedPronoun: string;
}

/**
 * German possessive pronouns decline by the case and grammatical gender of the
 * noun they modify. Each key targets a specific (case, noun-gender) pair that
 * actually appears in our translation strings.
 *
 *   accusative feminine  → "seine Box"  / "ihre Box"
 *   accusative neuter    → "sein Gewicht" / "ihr Gewicht"
 *   dative feminine      → "in seiner Probierbox" / "in ihrer Probierbox"
 *   dative plural        → "von seinen Rezepturen" / "von ihren Rezepturen"
 */
interface IPossessiveCases {
  accFem: string;
  accNeut: string;
  datFem: string;
  datPlural: string;
}

const EN_PRONOUNS: Record<Gender, IPronouns> = {
  BOY: { pronoun: "he", possessivePronoun: "his", objectPronoun: "him", contractedPronoun: "he's" },
  GIRL: { pronoun: "she", possessivePronoun: "her", objectPronoun: "her", contractedPronoun: "she's" },
  UNKNOWN: { pronoun: "they", possessivePronoun: "their", objectPronoun: "them", contractedPronoun: "they're" },
};

const DE_PRONOUNS: Record<Gender, IPronouns> = {
  BOY: { pronoun: "er", possessivePronoun: "sein", objectPronoun: "ihn", contractedPronoun: "er ist" },
  GIRL: { pronoun: "sie", possessivePronoun: "ihr", objectPronoun: "sie", contractedPronoun: "sie ist" },
  UNKNOWN: { pronoun: "sie", possessivePronoun: "ihr", objectPronoun: "sie", contractedPronoun: "sie ist" },
};

const DE_POSSESSIVE_CASES: Record<Gender, IPossessiveCases> = {
  BOY: { accFem: "seine", accNeut: "sein", datFem: "seiner", datPlural: "seinen" },
  GIRL: { accFem: "ihre", accNeut: "ihr", datFem: "ihrer", datPlural: "ihren" },
  UNKNOWN: { accFem: "ihre", accNeut: "ihr", datFem: "ihrer", datPlural: "ihren" },
};

const PRONOUNS_BY_LOCALE: Record<string, Record<Gender, IPronouns>> = {
  en: EN_PRONOUNS,
  de: DE_PRONOUNS,
};

const getPronounsForLocale = (locale: string): Record<Gender, IPronouns> => {
  const lang = locale.split("-")[0];
  return PRONOUNS_BY_LOCALE[lang] ?? EN_PRONOUNS;
};

const getPossessiveCases = (
  gender: Gender,
  locale: string,
  fallback: string,
): IPossessiveCases => {
  if (locale.startsWith("de")) {
    return DE_POSSESSIVE_CASES[gender];
  }
  return { accFem: fallback, accNeut: fallback, datFem: fallback, datPlural: fallback };
};

export const getInterpolationVariables = ({ name, gender, names, locale = "en-GB" }:
{ name?: string; gender?: Gender; names?: string[]; locale?: string }) => {
  const resolvedGender = gender ?? "UNKNOWN";
  const pronouns = getPronounsForLocale(locale)[resolvedGender];
  const cases = getPossessiveCases(resolvedGender, locale, pronouns.possessivePronoun);
  const catNamesList = names ?? [];

  return {
    pronoun: pronouns.pronoun,
    possessivePronoun: pronouns.possessivePronoun,
    possessivePronounAccFem: cases.accFem,
    possessivePronounAccNeut: cases.accNeut,
    possessivePronounDatFem: cases.datFem,
    possessivePronounDatPlural: cases.datPlural,
    objectPronoun: pronouns.objectPronoun,
    contractedPronoun: pronouns.contractedPronoun,
    catName: name ?? "",
    catNamePossessive: name ? formatPossessiveName(name, locale) : "",
    catNames: formatCatNames(catNamesList, locale) ?? "",
    catNamesPossessive: formatPossessiveCatNames(catNamesList, locale) ?? "",
  };
};
